
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import logger from "@/lib/logger";
import NewsletterSignup from "@/components/blog/NewsletterSignup";
import { useAuth } from "@/contexts/AuthContext";
import { BlogPostData } from "@/types/blog";
import { formatDate, getExcerpt } from "@/utils/blogUtils";
import BlogPostContent from "@/components/blog/BlogPostContent";
import BlogPostLoader from "@/components/blog/BlogPostLoader";
import BlogPostNotFound from "@/components/blog/BlogPostNotFound";
import BlogPostStyles from "@/components/blog/BlogPostStyles";
import { useIsMobile } from "@/hooks/use-mobile";
import FeedbackQuestion from "@/components/shared/FeedbackQuestion";

const BlogPost = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setIsLoading(true);
        setNotFound(false);
        
        if (!id) {
          setNotFound(true);
          return;
        }
        
        logger.log('Fetching blog post with ID:', id);
        
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        
        if (error) {
          logger.error('Supabase error:', error);
          throw error;
        }
        
        logger.log('Blog post data:', data);
        
        if (!data) {
          logger.log('Blog post not found');
          setNotFound(true);
          return;
        }
        
        const direction = data.text_direction === "rtl" ? "rtl" : "ltr";
        
        setPost({
          ...data,
          text_direction: direction as "ltr" | "rtl"
        });
      } catch (error: any) {
        logger.error('Error fetching blog post:', error.message);
        toast({
          title: "Failed to load blog post",
          description: error.message,
          variant: "destructive"
        });
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogPost();
  }, [id, toast]);

  return (
    <Layout
      title={post?.title}
      description={post?.content ? getExcerpt(post.content) : undefined}
      image={post?.image_url || undefined}
      type="article"
      path={`/blog/${id}`}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/blog">
          <Button variant="ghost" className={`mb-6 ${post?.text_direction === 'rtl' ? 'pr-0 mr-auto' : 'pl-0'} hover:bg-transparent`}>
            <ArrowLeft className={`${post?.text_direction === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4`} /> 
            Back to all posts
          </Button>
        </Link>
        
        {isLoading ? (
          <BlogPostLoader />
        ) : notFound ? (
          <BlogPostNotFound />
        ) : post ? (
          <article dir={post.text_direction || 'ltr'} className={isMobile ? "text-base" : ""}>
            <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${post.text_direction === 'rtl' ? 'text-right' : ''}`}>{post.title}</h1>
            <div className={`flex items-center text-gray-500 mb-6 sm:mb-8 ${post.text_direction === 'rtl' ? 'justify-end' : ''}`}>
              <span className={post.text_direction === 'rtl' ? 'ml-4' : 'mr-4'}>{formatDate(post.created_at)}</span>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded-full text-gray-700">
                {post.category}
              </span>
            </div>
            
            {post.image_url && (
              <div className="mb-6 sm:mb-8 rounded-lg overflow-hidden">
                <div className="w-full">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      logger.error('Image failed to load:', post.image_url);
                      const target = e.target as HTMLImageElement;
                      if (!target.src.includes('?')) {
                        target.src = `${post.image_url}?${new Date().getTime()}`;
                      } else {
                        target.src = "/placeholder.svg";
                      }
                    }}
                  />
                </div>
              </div>
            )}
            
            <BlogPostContent 
              content={post.content} 
              textDirection={post.text_direction} 
              isLoggedIn={isLoggedIn} 
            />
            
            {post.id && <FeedbackQuestion itemId={post.id} itemType="blog" />}
            
            <div className="mt-12 pt-8 border-t border-gray-200 space-y-8">
              <NewsletterSignup blogPostId={post.id} />
            </div>
          </article>
        ) : null}
      </div>
      
      <BlogPostStyles />
    </Layout>
  );
};

export default BlogPost;
